import React, { useState, useCallback, useRef, useEffect } from 'react';
import './App.css';
import Header from './components/Header';
import ImageGrid from './components/ImageGrid';
import Controls from './components/Controls';
import ProgressBar from './components/ProgressBar';
import ConfirmationDialog from './components/ConfirmationDialog';
import CustomPromptModal from './components/CustomPromptModal';
import SuccessDialog from './components/SuccessDialog';
import LicenseDialog from './components/LicenseDialog';
import Lightbox from './components/Lightbox';
import TestDragDrop from './components/TestDragDrop';
import ValidationDialog from './components/ValidationDialog';
import AILimitDialog from './components/AILimitDialog';
import { processImages, uploadToS3 } from './utils/imageProcessor';

function App() {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imageMetadata, setImageMetadata] = useState({});
  // Bumped after in-place edits (marquee crop) to cache-bust image URLs
  const [imageVersions, setImageVersions] = useState({});
  const [selectedImageIds, setSelectedImageIds] = useState(new Set());
  // Default: date modified, OLDEST first — the most recent capture/crop/retouch
  // always lands at the END of the grid (the last action performed).
  const [sortBy, setSortBy] = useState('modified');
  const [mode, setMode] = useState('resize'); // Track resize/crop mode
  const [settings, setSettings] = useState({
    targetSize: '',
    shortEdgeSize: '',
    format: 'avif',
    quality: 57,
    prefix: '-pixl',
    enableUpscaling: false,
    cropWidth: '',
    cropHeight: '',
    cropPreset: 'custom',
    enableAIFill: false,
    useSystemPrompt: true,
    customPrompt: '',
    cropAlignment: 'center',
    // Deliberately NOT persisted: a destructive mode should never silently
    // survive an app restart.
    overwriteOriginals: false
  });
  const [iterations, setIterations] = useState(1);
  const iterationsRef = useRef(1); // Add ref to always have current value
  const [isProcessing, setIsProcessing] = useState(false);
  const processingCancelledRef = useRef(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [csvFilePath, setCsvFilePath] = useState(null);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [conversionCount, setConversionCount] = useState(0);
  const [successDialog, setSuccessDialog] = useState({ 
    isOpen: false, 
    message: '', 
    outputPath: null 
  });
  const [validationDialog, setValidationDialog] = useState({
    isOpen: false,
    message: ''
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [awsCredentials, setAwsCredentials] = useState({
    bucketName: 'pixlevents',
    folderPath: 'pixlsizer',
    region: 'us-east-1'
  });
  const [aiLimitDialog, setAiLimitDialog] = useState({
    isOpen: false,
    limitInfo: null
  });
  // Overwrite Originals confirmation gate: action is 'folder' or 'cloud'
  const [overwriteConfirm, setOverwriteConfirm] = useState({
    isOpen: false,
    action: null
  });
  // AI Fill prompt gate: Save to Folder with AI Fill active always asks for
  // the fill/outpaint prompt first (empty = use the built-in default prompt)
  const [fillPromptModal, setFillPromptModal] = useState(false);
  const pendingFillPromptRef = useRef(null);

  // Debug: Check if electronAPI is available
  useEffect(() => {
    console.log('App mounted. Checking window.electronAPI...');
    console.log('window.electronAPI:', window.electronAPI);
    if (!window.electronAPI) {
      console.error('CRITICAL: window.electronAPI is not available! Preload script may not be working.');
      // Try to check after a delay in case it's a timing issue
      setTimeout(() => {
        console.log('Rechecking window.electronAPI after 1 second...');
        console.log('window.electronAPI:', window.electronAPI);
      }, 1000);
    } else {
      console.log('electronAPI is available with methods:', Object.keys(window.electronAPI));
    }
  }, []);

  // Shared tail of every import path (picker, drag-drop, browser, URL):
  // HEIC/HEIF are converted to JPEG and TGA to PNG first (Chromium/Sharp can't
  // decode either),
  // then metadata is fetched and the files land in the grid, selected.
  //
  // Large batches (and TGA/HEIC conversions of big renders) can take a long
  // time, so this shows an "Importing… N of M" overlay and adds each image to
  // the grid AS IT FINISHES rather than all at once at the end — the UI must
  // never look frozen. A few files are processed concurrently.
  const [importStatus, setImportStatus] = useState(null); // {done,total,current}
  const addPathsToGrid = useCallback(async (rawPaths) => {
    if (!rawPaths || rawPaths.length === 0) return;

    const total = rawPaths.length;
    let done = 0;
    const failures = [];
    setImportStatus({ done: 0, total, current: rawPaths[0].split(/[/\\]/).pop() });

    const importOne = async (rawPath) => {
      try {
        let filePath = rawPath;
        if (/\.(heic|heif|tga)$/i.test(rawPath)) {
          const normalized = await window.electronAPI.normalizeImagePath(rawPath);
          if (!normalized.success) throw new Error(normalized.error || 'conversion failed');
          filePath = normalized.filePath;
        }
        const metadata = await window.electronAPI.getImageMetadata(filePath);
        if (metadata.success) {
          setImageMetadata(prev => ({
            ...prev,
            [filePath]: {
              width: metadata.width,
              height: metadata.height,
              format: metadata.format,
              fileSize: metadata.fileSize,
              modifiedAt: metadata.modifiedAt,
              fileName: metadata.fileName
            }
          }));
        }
        setSelectedFiles(prev => (prev.includes(filePath) ? prev : [...prev, filePath]));
        setSelectedImageIds(prev => new Set([...prev, filePath]));
      } catch (err) {
        console.error('Import failed:', rawPath, err);
        failures.push(`${rawPath.split(/[/\\]/).pop()}: ${err.message}`);
      } finally {
        done += 1;
        setImportStatus({ done, total, current: null });
      }
    };

    // Small worker pool: keeps the main process busy without flooding it
    const queue = [...rawPaths];
    const worker = async () => {
      while (queue.length) {
        const next = queue.shift();
        setImportStatus(s => (s ? { ...s, current: next.split(/[/\\]/).pop() } : s));
        await importOne(next);
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, total) }, worker));

    setImportStatus(null);
    if (failures.length) {
      alert(`Could not import ${failures.length} file${failures.length > 1 ? 's' : ''}:\n\n${failures.slice(0, 8).join('\n')}${failures.length > 8 ? '\n…' : ''}`);
    }
  }, []);

  const selectFiles = useCallback(async () => {
    try {
      const result = await window.electronAPI.selectFiles();
      if (!result.canceled && result.filePaths) {
        console.log('Files selected:', result.filePaths);
        await addPathsToGrid(result.filePaths);
      }
    } catch (error) {
      console.error('Error selecting files:', error);
      alert('Error selecting files: ' + error.message);
    }
  }, [addPathsToGrid]);

  const handleFilesDropped = useCallback(async (files) => {
    try {
      console.log('Dropped files:', files);
      console.log('window.electronAPI:', window.electronAPI);
      console.log('First file:', files[0]);

      // Use webUtils.getPathForFile() to get file paths in modern Electron
      const filePaths = [];

      for (const file of files) {
        // Try multiple approaches to get the file path
        // 1. Direct path property (older Electron or some configurations)
        if (file.path) {
          console.log('Using file.path:', file.path);
          filePaths.push(file.path);
          continue;
        }

        // 2. Try webkitRelativePath (sometimes available)
        if (file.webkitRelativePath) {
          console.log('Using webkitRelativePath:', file.webkitRelativePath);
          filePaths.push(file.webkitRelativePath);
          continue;
        }

        // 3. Try electronAPI.getPathForFile if available
        if (window.electronAPI && window.electronAPI.getPathForFile) {
          try {
            const path = window.electronAPI.getPathForFile(file);
            if (path) {
              console.log('Got path from electronAPI:', path);
              filePaths.push(path);
              continue;
            }
          } catch (err) {
            console.error('Error using getPathForFile:', err);
          }
        }

        // 4. Browser-dragged image: no local path, but the File object
        // carries the actual bytes — save them to the temp imports folder
        // via the main process and use that path.
        try {
          const buffer = await file.arrayBuffer();
          const result = await window.electronAPI.importDroppedImage(file.name, file.type, buffer);
          if (result.success) {
            console.log('Imported browser-dropped image:', result.filePath);
            filePaths.push(result.filePath);
            continue;
          }
          console.warn('Browser image import failed:', result.error);
        } catch (importErr) {
          console.error('Browser image import error:', importErr);
        }

        console.warn('No path available for file:', file.name, 'File object:', file);
      }

      if (filePaths.length === 0) {
        alert('Unable to process dropped files. Please use the "Select Images" button instead.');
        return;
      }

      await addPathsToGrid(filePaths);
    } catch (error) {
      console.error('Error handling dropped files:', error);
      alert('Error handling dropped files: ' + error.message);
    }
  }, [addPathsToGrid]);

  // Image dragged from a browser as a URL only (no File payload) — download
  // it into the temp imports folder and add it like any other image.
  const handleUrlsDropped = useCallback(async (urls) => {
    try {
      const filePaths = [];
      const errors = [];
      for (const url of urls) {
        const result = await window.electronAPI.importImageFromUrl(url);
        if (result.success) {
          filePaths.push(result.filePath);
        } else {
          errors.push(result.error);
        }
      }
      await addPathsToGrid(filePaths);
      if (filePaths.length === 0 && errors.length > 0) {
        alert(`Could not import the dropped image: ${errors[0]}`);
      }
    } catch (error) {
      console.error('Error importing dropped URL:', error);
      alert('Could not import the dropped image: ' + error.message);
    }
  }, [addPathsToGrid]);

  const clearFiles = useCallback(() => {
    setShowResetDialog(true);
  }, []);

  const handleResetConfirm = useCallback(() => {
    console.log('=== CLEAR AND RESET TRIGGERED ===');
    console.log('Previous settings:', JSON.stringify(settings, null, 2));
    
    // Reset all images
    setSelectedFiles([]);
    setImageMetadata({});
    setImageVersions({});
    setSelectedImageIds(new Set());

    // Reset all settings to defaults
    const resetSettings = {
      targetSize: '',
      shortEdgeSize: '',
      format: 'avif',
      quality: 57,
      prefix: '-pixl',
      enableUpscaling: false,
      cropWidth: '',
      cropHeight: '',
      cropPreset: 'custom',
      enableAIFill: false,
      useSystemPrompt: true,
      customPrompt: '',
      cropAlignment: 'center',
      overwriteOriginals: false
    };

    console.log('Resetting to:', JSON.stringify(resetSettings, null, 2));
    setSettings(resetSettings);

    // Reset mode to resize
    setMode('resize');

    // Reset sort
    setSortBy('name');

    // Reset progress
    setProgress(0);

    // Reset CSV path
    setCsvFilePath(null);

    // Close dialog
    setShowResetDialog(false);
    
    console.log('=== RESET COMPLETE ===');
  }, [settings]);

  const handleResetCancel = useCallback(() => {
    setShowResetDialog(false);
  }, []);

  const handleSuccessClose = useCallback(() => {
    setSuccessDialog({ isOpen: false, message: '', outputPath: null });
  }, []);

  const handleViewInFolder = useCallback(async () => {
    if (successDialog.outputPath) {
      try {
        await window.electronAPI.openFolder(successDialog.outputPath);
        setSuccessDialog({ isOpen: false, message: '', outputPath: null });
      } catch (error) {
        console.error('Error opening folder:', error);
      }
    }
  }, [successDialog.outputPath]);

  const handleOpenCSV = useCallback(async () => {
    if (csvFilePath) {
      try {
        await window.electronAPI.openCSV(csvFilePath);
      } catch (error) {
        console.error('Error opening CSV:', error);
        alert('Error opening CSV file: ' + error.message);
      }
    }
  }, [csvFilePath]);

  const toggleImageSelection = useCallback((filePath, isSelected) => {
    setSelectedImageIds(prev => {
      const newSet = new Set(prev);
      if (isSelected) {
        newSet.add(filePath);
      } else {
        newSet.delete(filePath);
      }
      return newSet;
    });
  }, []);

  const selectAllImages = useCallback((selectAll = true) => {
    if (selectAll) {
      setSelectedImageIds(new Set(selectedFiles));
    } else {
      setSelectedImageIds(new Set());
    }
  }, [selectedFiles]);

  const handleProcess = useCallback(async () => {
    const selected = selectedFiles.filter(path => selectedImageIds.has(path));
    if (selected.length === 0) {
      alert('Please select at least one image to process.');
      return;
    }

    const hasCropDimensions = (settings.cropWidth || settings.cropHeight);
    
    // DEBUG: Log all settings being passed
    console.log('=== AI CROP FILL DEBUG - Processing Started ===');
    console.log('Current settings:', JSON.stringify(settings, null, 2));
    console.log('Selected files:', selected);
    console.log('License info:', licenseInfo ? { tier: licenseInfo.tier, features: Object.keys(licenseInfo.features || {}) } : 'None');
    console.log('Has crop dimensions:', hasCropDimensions);
    console.log('Crop Width:', settings.cropWidth, typeof settings.cropWidth);
    console.log('Crop Height:', settings.cropHeight, typeof settings.cropHeight);
    console.log('Enable AI Fill:', settings.enableAIFill, typeof settings.enableAIFill);
    console.log('Target Size:', settings.targetSize, typeof settings.targetSize);
    console.log('Short Edge Size:', settings.shortEdgeSize, typeof settings.shortEdgeSize);
    console.log('Format:', settings.format);
    console.log('Quality:', settings.quality);
    console.log('Prefix:', settings.prefix);
    console.log('Enable Upscaling:', settings.enableUpscaling);
    console.log('Crop Preset:', settings.cropPreset);
    
    if ((!settings.targetSize || settings.targetSize.trim() === '' || parseInt(settings.targetSize) <= 0) &&
        (!settings.shortEdgeSize || settings.shortEdgeSize.trim() === '' || parseInt(settings.shortEdgeSize) <= 0) &&
        !hasCropDimensions) {
      setValidationDialog({
        isOpen: true,
        message: 'Please enter a valid target size for either the long edge, short edge, or crop dimensions.'
      });
      return;
    }

    // Check if user has exceeded free tier limit
    const newCount = conversionCount + selected.length;
    if (licenseInfo?.tier === 'free' && newCount > 20) {
      const remaining = Math.max(0, 20 - conversionCount);
      if (remaining === 0) {
        alert('You have reached your free conversion limit of 20 images. Please upgrade to continue.');
        setShowLicenseDialog(true);
        return;
      } else {
        alert(`You can only convert ${remaining} more images with your free account. Please upgrade to process all selected images.`);
        return;
      }
    }

    // Check batch limit for Basic plan (250 images per batch)
    if (licenseInfo?.tier === 'basic' && selected.length > 250) {
      alert(`Basic plan is limited to 250 images per batch. You have selected ${selected.length} images. Please deselect ${selected.length - 250} images or upgrade to Pro/Agency for larger batches.`);
      return;
    }

    try {
      // Overwrite mode writes back into each image's OWN folder — asking for a
      // destination here would be contradictory (the user already confirmed the
      // overwrite in the dialog), so skip the folder picker entirely.
      let outputDirectory;
      if (settings.overwriteOriginals) {
        const firstFile = selected[0];
        const sepIdx = Math.max(firstFile.lastIndexOf('/'), firstFile.lastIndexOf('\\'));
        outputDirectory = sepIdx > 0 ? firstFile.slice(0, sepIdx) : firstFile;
        console.log('Overwrite mode: skipping folder picker, writing to source folders');
      } else {
        const result = await window.electronAPI.selectOutputDirectory();
        if (result.canceled) return;
        outputDirectory = result.directoryPath;
        console.log('Output directory selected:', outputDirectory);
      }

      setIsProcessing(true);
      setProgress(0);
      setProgressMessage('');

      // DEBUG: Log exactly what we're passing to processImages
      console.log('=== Calling processImages with these parameters ===');
      console.log('Selected files:', selected);
      console.log('Output directory:', outputDirectory);
      console.log('Settings object being passed:', JSON.stringify(settings, null, 2));
      console.log('License info being passed:', licenseInfo ? { tier: licenseInfo.tier, features: licenseInfo.features } : 'None');

      processingCancelledRef.current = false;

      // Use iterationsRef.current to get the most current value
      console.log('Using iterations value:', iterationsRef.current);

      // Apply the prompt from the save-time AI Fill modal (ref avoids a
      // stale-settings race with the setSettings above)
      const fillOverride = pendingFillPromptRef.current;
      pendingFillPromptRef.current = null;
      const effectiveSettings = fillOverride ? { ...settings, ...fillOverride } : settings;

      await processImages(
        selected,
        outputDirectory,
        effectiveSettings,
        (progress, message) => {
          console.log(`Progress: ${progress}% - ${message}`);
          setProgress(progress);
          if (message) setProgressMessage(message);
        },
        licenseInfo,
        () => processingCancelledRef.current,
        iterationsRef.current  // Use ref value instead of state
      );

      console.log('=== Processing completed successfully ===');
      setIsProcessing(false);
      
      // Update conversion count
      const updatedCount = conversionCount + selected.length;
      setConversionCount(updatedCount);
      localStorage.setItem('pixlsizer_conversions', updatedCount.toString());

      // Show success dialog
      if (licenseInfo?.tier === 'free' && updatedCount >= 20) {
        setSuccessDialog({
          isOpen: true,
          message: `Successfully processed ${selected.length} images! You've now used all 20 free conversions. Please upgrade to continue using PixlSizer.`,
          outputPath: outputDirectory
        });
        setShowLicenseDialog(true);
      } else {
        const remaining = licenseInfo?.tier === 'free' ? 20 - updatedCount : 'unlimited';
        setSuccessDialog({
          isOpen: true,
          message: settings.overwriteOriginals
            ? `Successfully processed ${selected.length} images — originals overwritten in place.`
            : `Successfully processed ${selected.length} images! ${licenseInfo?.tier === 'free' ? `Remaining free conversions: ${remaining}` : ''}`,
          outputPath: outputDirectory
        });
      }
    } catch (error) {
      setIsProcessing(false);
      console.error('=== Processing error occurred ===');
      console.error('Error details:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      
      // Check if this was a user cancellation
      if (error.message === 'Processing cancelled by user') {
        alert('The batch operation has been interrupted');
      } else if (error.limitExceeded) {
        // Handle AI usage limit exceeded
        setAiLimitDialog({
          isOpen: true,
          limitInfo: error.limitInfo
        });
      } else {
        alert('Error processing images: ' + error.message);
      }
    }
  }, [selectedFiles, selectedImageIds, settings, conversionCount, licenseInfo]);

  const handleProcessAndUpload = useCallback(async () => {
    // Check Pro, Agency, or Partner license before allowing cloud upload
    if (!licenseInfo || !['pro', 'agency', 'partner'].includes(licenseInfo.tier) || !licenseInfo.features?.cloudUpload) {
      alert('Cloud upload requires a Pro, Agency, or Partner license. Please upgrade your license to access this feature.');
      return;
    }

    const selected = selectedFiles.filter(path => selectedImageIds.has(path));
    if (selected.length === 0) {
      alert('Please select at least one image to process.');
      return;
    }

    // Check batch limit for Basic plan (250 images per batch) - though Basic can't use cloud upload anyway
    if (licenseInfo?.tier === 'basic' && selected.length > 250) {
      alert(`Basic plan is limited to 250 images per batch. You have selected ${selected.length} images. Please deselect ${selected.length - 250} images or upgrade to Pro/Agency for larger batches.`);
      return;
    }

    const hasCropDimensions = (settings.cropWidth || settings.cropHeight);
    
    if ((!settings.targetSize || settings.targetSize.trim() === '' || parseInt(settings.targetSize) <= 0) &&
        (!settings.shortEdgeSize || settings.shortEdgeSize.trim() === '' || parseInt(settings.shortEdgeSize) <= 0) &&
        !hasCropDimensions) {
      setValidationDialog({
        isOpen: true,
        message: 'Please enter a valid target size for either the long edge, short edge, or crop dimensions.'
      });
      return;
    }

    try {
      const result = await window.electronAPI.selectOutputDirectory();
      if (result.canceled) return;

      setIsProcessing(true);
      setProgress(0);
      setProgressMessage('');

      // AWS is configured server-side via Vercel environment variables

      // Process and upload
      processingCancelledRef.current = false;
      const uploadResult = await uploadToS3(
        selected,
        result.directoryPath,
        settings,
        awsCredentials,
        (progress, message) => {
          setProgress(progress);
          if (message) setProgressMessage(message);
        },
        licenseInfo,
        () => processingCancelledRef.current,
        iterationsRef.current  // Use ref value instead of state
      );

      setIsProcessing(false);
      setCsvFilePath(uploadResult.csvPath);
      setSuccessDialog({
        isOpen: true,
        message: `Successfully processed and uploaded ${selected.length} images!`,
        outputPath: result.directoryPath
      });
    } catch (error) {
      setIsProcessing(false);
      console.error('Process and upload error:', error);

      // Check if this was a user cancellation
      if (error.message === 'Processing cancelled by user') {
        alert('The batch operation has been interrupted');
      } else if (error.limitExceeded) {
        // Handle AI usage limit exceeded
        setAiLimitDialog({
          isOpen: true,
          limitInfo: error.limitInfo
        });
      } else if (error.message && error.message.toLowerCase().includes('license')) {
        // NEVER clear the license on an upload error - it's almost always a
        // temporary server/network issue, not an invalid license. Ambassador
        // licenses in particular must never be reset by a cloud-upload failure.
        // Just show a soft retry message; do NOT tell them to re-activate.
        alert('There was a temporary issue with the cloud upload. Please try again in a moment.');
      } else {
        alert('Error processing and uploading images: ' + error.message);
      }
    }
  }, [selectedFiles, selectedImageIds, settings, awsCredentials, licenseInfo]);

  // Continue the Save to Folder chain after any prompt gate
  const continueProcessFlow = useCallback(() => {
    if (settings.overwriteOriginals) {
      setOverwriteConfirm({ isOpen: true, action: 'folder' });
      return;
    }
    handleProcess();
  }, [settings.overwriteOriginals, handleProcess]);

  // Save to Folder gates, in order:
  // 1. AI Fill active -> ask for the fill/outpaint prompt (modal)
  // 2. Overwrite Originals -> destructive-action confirmation
  const requestProcess = useCallback(() => {
    const aiFillActive = mode === 'crop' && settings.enableAIFill &&
      (settings.cropWidth || settings.cropHeight);
    if (aiFillActive) {
      setFillPromptModal(true);
      return;
    }
    continueProcessFlow();
  }, [mode, settings.enableAIFill, settings.cropWidth, settings.cropHeight, continueProcessFlow]);

  // Prompt entered (or left empty for the default) — stash it and continue
  const handleFillPromptSave = useCallback((prompt) => {
    setFillPromptModal(false);
    const trimmed = (prompt || '').trim();
    // Empty prompt = use the built-in default; non-empty = custom
    pendingFillPromptRef.current = { customPrompt: trimmed, useSystemPrompt: trimmed === '' };
    // Keep settings in sync so the prompt is remembered for next time
    setSettings(prev => ({ ...prev, customPrompt: trimmed || prev.customPrompt, useSystemPrompt: trimmed === '' }));
    continueProcessFlow();
  }, [continueProcessFlow]);

  const requestProcessAndUpload = useCallback(() => {
    if (settings.overwriteOriginals) {
      setOverwriteConfirm({ isOpen: true, action: 'cloud' });
      return;
    }
    handleProcessAndUpload();
  }, [settings.overwriteOriginals, handleProcessAndUpload]);

  const handleOverwriteConfirm = useCallback(() => {
    const action = overwriteConfirm.action;
    setOverwriteConfirm({ isOpen: false, action: null });
    if (action === 'cloud') {
      handleProcessAndUpload();
    } else {
      handleProcess();
    }
  }, [overwriteConfirm.action, handleProcess, handleProcessAndUpload]);

  // Revalidate cached license with API
  const revalidateCachedLicense = useCallback(async () => {
    try {
      const cached = localStorage.getItem('pixlsizer_license');
      if (!cached) return false;

      const licenseData = JSON.parse(cached);
      if (!licenseData.token) return false;

      // Revalidate with API
      const validationData = {
        token: licenseData.token,
        ...(licenseData.email && { email: licenseData.email })
      };

      const result = await window.electronAPI.validateLicense(validationData);

      if (result.success) {
        // Update cached license with fresh data
        const updatedLicense = {
          token: licenseData.token,
          tier: result.data.tier,
          features: result.data.features,
          validatedAt: Date.now(),
          expiresAt: result.data.expiresAt,
          email: result.data.email || licenseData.email
        };

        localStorage.setItem('pixlsizer_license', JSON.stringify(updatedLicense));
        setLicenseInfo(updatedLicense);
        return true;
      } else {
        // API returned invalid - BUT for Ambassador/Partner, keep the cached license
        // This could be a temporary server issue, don't punish the user
        if (licenseData.tier === 'partner' || licenseData.token?.startsWith('PIXL-AMBASSADOR-')) {
          console.log('Ambassador license revalidation failed, but keeping cached license');
          setLicenseInfo(licenseData);
          return true; // Keep using cached license
        }
        // For regular licenses, also keep cached but return false to indicate issue
        console.log('License revalidation failed, keeping cached license anyway');
        setLicenseInfo(licenseData);
        return true; // Don't clear - could be temporary server issue
      }
    } catch (error) {
      console.error('License revalidation error:', error);
      // NEVER clear license on network/API errors - just use cached version
      const cached = localStorage.getItem('pixlsizer_license');
      if (cached) {
        const licenseData = JSON.parse(cached);
        setLicenseInfo(licenseData);
        return true; // Keep using cached license
      }
      return false;
    }
  }, []);

  // License management functions
  const checkLicense = useCallback(async () => {
    try {
      // Check for cached license first
      const cached = localStorage.getItem('pixlsizer_license');
      if (cached) {
        const licenseData = JSON.parse(cached);

        // Partner/Ambassador licenses get 30 days cache, regular licenses get 7 days
        const isPartner = licenseData.tier === 'partner' || licenseData.token?.startsWith('PIXL-AMBASSADOR-');
        const maxCacheAge = isPartner ? 30 * 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;

        const cacheAge = Date.now() - licenseData.validatedAt;
        if (cacheAge < maxCacheAge) {
          // Cache still valid, use it directly
          setLicenseInfo(licenseData);
          return;
        } else {
          // Cache expired, try to revalidate (but won't clear on failure)
          console.log('License cache expired, attempting background revalidation...');
          await revalidateCachedLicense();
          // revalidateCachedLicense now ALWAYS keeps cached license, so we're done
          return;
        }
      }

      // No cached license at all, show license dialog
      setShowLicenseDialog(true);
    } catch (error) {
      console.error('Error checking license:', error);
      // If we have ANY cached license, use it instead of showing dialog
      const cached = localStorage.getItem('pixlsizer_license');
      if (cached) {
        try {
          const licenseData = JSON.parse(cached);
          setLicenseInfo(licenseData);
          return;
        } catch (e) {
          // Parse error, show dialog
        }
      }
      setShowLicenseDialog(true);
    }
  }, [revalidateCachedLicense]);

  const handleLicenseValidated = useCallback((licenseData) => {
    // Store the license info in localStorage
    const licenseToStore = {
      token: licenseData.token,
      tier: licenseData.tier,
      features: licenseData.features,
      validatedAt: Date.now(),
      expiresAt: licenseData.expiresAt,
      email: licenseData.email
    };
    localStorage.setItem('pixlsizer_license', JSON.stringify(licenseToStore));
    
    // Sync to main process for upscaling
    window.electronAPI.syncLicense(licenseToStore).then(result => {
      if (result.success) {
        console.log('License synced to main process after activation');
      }
    }).catch(err => {
      console.error('License sync error:', err);
    });
    
    setLicenseInfo(licenseData);
    setShowLicenseDialog(false);
  }, []);

  const handleLicenseDialogClose = useCallback(async () => {
    // If user cancels after hitting the 20 conversion limit, close the app
    if (conversionCount >= 20 && window.electronAPI) {
      try {
        await window.electronAPI.closeApp();
      } catch (error) {
        console.error('Error closing app:', error);
        window.close(); // Fallback
      }
    } else {
      setShowLicenseDialog(false);
    }
  }, [conversionCount]);

  const handleDeactivateLicense = useCallback(async () => {
    if (!licenseInfo || licenseInfo.tier === 'free') {
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to deactivate your ${licenseInfo.tier.toUpperCase()} license?\n\n` +
      'This will:\n' +
      '• Remove all Pro/Agency features\n' +
      '• Allow the license to be used on another device\n' +
      '• Reset your app to free tier (20 conversion limit)\n\n' +
      'You can reactivate the same license later if needed.'
    );

    if (!confirmed) return;

    try {
      if (!window.electronAPI) {
        alert('License deactivation requires the desktop application.');
        return;
      }

      // Get the current license token 
      const cachedLicense = JSON.parse(localStorage.getItem('pixlsizer_license') || '{}');
      const token = cachedLicense.token;
      
      if (!token) {
        alert('No active license found to deactivate.');
        return;
      }

      console.log('Attempting to deactivate license:', token);
      const result = await window.electronAPI.deactivateLicense(token);
      console.log('Deactivation result:', result);
      
      if (result.success) {
        // Clear local license data
        localStorage.removeItem('pixlsizer_license');
        
        // Reset to free tier
        setLicenseInfo({
          tier: 'free',
          features: {
            localProcessing: true,
            cloudUpload: false,
            csvReporting: false,
            batchProcessing: true,
            formatSupport: ['webp', 'jpeg', 'png'],
            maxImages: 20,
            remainingImages: Math.max(0, 20 - conversionCount)
          }
        });
        
        alert('License deactivated successfully! You can now use this license on another device.');
      } else {
        // More detailed error message
        const errorMsg = `Failed to deactivate license: ${result.error || 'Unknown error'}\n\nTip: If you continue having issues, you can manually clear your license by deleting:\nC:\\Users\\%USERNAME%\\AppData\\Roaming\\PixlSizer`;
        alert(errorMsg);
      }
    } catch (error) {
      console.error('License deactivation error:', error);
      alert('Error deactivating license: ' + error.message);
    }
  }, [licenseInfo, conversionCount]);

  // Initialize conversion count and check license on app startup
  useEffect(() => {
    // Load conversion count from localStorage
    const storedCount = parseInt(localStorage.getItem('pixlsizer_conversions') || '0');
    setConversionCount(storedCount);

    // Check if running in browser instead of Electron
    if (!window.electronAPI) {
      console.log('Running in web browser - Electron APIs not available');
      setLicenseInfo({
        tier: 'demo',
        features: {
          localProcessing: false,
          cloudUpload: false,
          csvReporting: false,
          batchProcessing: false,
          formatSupport: [],
          maxImages: 0
        }
      });
      return;
    }
    
    // Development bypass - remove in production!
    if (process.env.NODE_ENV === 'development') {
      console.log('Development mode - bypassing license check');
      setLicenseInfo({
        tier: 'pro',
        features: {
          localProcessing: true,
          cloudUpload: true,
          csvReporting: true,
          batchProcessing: true,
          formatSupport: ['avif', 'webp', 'jpeg', 'png'],
          maxImages: 1000
        }
      });
      return;
    }

    // Check for stored license first
    const storedLicense = localStorage.getItem('pixlsizer_license');
    if (storedLicense) {
      try {
        const license = JSON.parse(storedLicense);
        setLicenseInfo(license);
        
        // Sync license to main process for upscaling feature
        window.electronAPI.syncLicense(license).then(result => {
          if (result.success) {
            console.log('License synced to main process for upscaling');
          } else {
            console.error('Failed to sync license:', result.error);
          }
        }).catch(err => {
          console.error('License sync error:', err);
        });
        
        return; // Exit early if we have a valid stored license
      } catch (error) {
        console.error('Error parsing stored license:', error);
        localStorage.removeItem('pixlsizer_license');
      }
    }
    
    // If under 20 conversions and no stored license, give free tier access
    if (storedCount < 20) {
      setLicenseInfo({
        tier: 'free',
        features: {
          localProcessing: true,
          cloudUpload: false,
          csvReporting: false,
          batchProcessing: true,
          formatSupport: ['webp', 'jpeg', 'png'],
          maxImages: 20,
          remainingImages: 20 - storedCount
        }
      });
    } else {
      // After 20 conversions, check for license or show dialog
      checkLicense();
    }
  }, [checkLicense]);

  // Sort files based on current sort setting
  const sortedFiles = React.useMemo(() => {
    const files = [...selectedFiles];
    
    const byName = (a, b) =>
      (imageMetadata[a]?.fileName || '').localeCompare(imageMetadata[b]?.fileName || '');

    switch (sortBy) {
      case 'modified':
        // Ascending: oldest first, newest at the end (ties fall back to name)
        return files.sort((a, b) => {
          const tA = imageMetadata[a]?.modifiedAt || 0;
          const tB = imageMetadata[b]?.modifiedAt || 0;
          return (tA - tB) || byName(a, b);
        });
      case 'name':
        return files.sort((a, b) => {
          const nameA = imageMetadata[a]?.fileName || '';
          const nameB = imageMetadata[b]?.fileName || '';
          return nameA.localeCompare(nameB);
        });
      case 'size':
        return files.sort((a, b) => {
          const sizeA = imageMetadata[a]?.fileSize || 0;
          const sizeB = imageMetadata[b]?.fileSize || 0;
          return sizeB - sizeA; // Descending
        });
      case 'dimensions':
        return files.sort((a, b) => {
          const metaA = imageMetadata[a];
          const metaB = imageMetadata[b];
          const areaA = metaA ? metaA.width * metaA.height : 0;
          const areaB = metaB ? metaB.width * metaB.height : 0;
          return areaB - areaA; // Descending
        });
      default:
        return files;
    }
  }, [selectedFiles, imageMetadata, sortBy]);

  // Add ESC key handler for cancelling processing
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isProcessing) {
        processingCancelledRef.current = true;
        setProgressMessage('Cancelling...');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isProcessing]);

  // Add document-level drag and drop handlers to prevent default rejection
  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Prevent default drag behaviors on document
    document.addEventListener('dragenter', preventDefaults, false);
    document.addEventListener('dragover', preventDefaults, false);
    document.addEventListener('dragleave', preventDefaults, false);
    document.addEventListener('drop', preventDefaults, false);

    return () => {
      document.removeEventListener('dragenter', preventDefaults);
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('dragleave', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
    };
  }, []);

  // Lightbox handlers
  const handleImageClick = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const handleLightboxClose = useCallback(() => {
    setLightboxOpen(false);
  }, []);

  const handleLightboxNavigate = useCallback((direction) => {
    if (direction === 'prev' && lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    } else if (direction === 'next' && lightboxIndex < sortedFiles.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  }, [lightboxIndex, sortedFiles.length]);

  // Generative retouch OR marquee crop produced a NEW sibling file — add it
  // to the grid as
  // its own thumbnail (auto-selected) so original and retouch sit side by side.
  // After a crop/retouch the new sibling file is appended to the grid; once
  // it has been sorted into place, move the (still open) lightbox onto it so
  // the result is shown live. Sorting by Date Modified puts it at the END, so
  // the old "same index" coincidence from name-sort no longer applies.
  const pendingLightboxPathRef = useRef(null);
  useEffect(() => {
    const pending = pendingLightboxPathRef.current;
    if (!pending) return;
    const idx = sortedFiles.indexOf(pending);
    if (idx !== -1) {
      pendingLightboxPathRef.current = null;
      setLightboxIndex(idx);
    }
  }, [sortedFiles]);

  const handleRetouched = useCallback((result) => {
    const newPath = result.outputPath;
    pendingLightboxPathRef.current = newPath;
    setSelectedFiles(prev => prev.includes(newPath) ? prev : [...prev, newPath]);
    setImageMetadata(prev => ({
      ...prev,
      [newPath]: {
        width: result.width,
        height: result.height,
        format: result.format,
        fileSize: result.fileSize,
        modifiedAt: result.modifiedAt,
        fileName: result.fileName
      }
    }));
    setSelectedImageIds(prev => new Set([...prev, newPath]));
  }, []);

  // Reveal a file in Explorer/Finder
  const handleShowInFolder = useCallback((filePath) => {
    window.electronAPI.showInFolder(filePath);
  }, []);

  // Styled delete-flow dialog (never native Windows alerts):
  // mode 'fallback' = Recycle Bin refused, offer permanent delete
  // mode 'error'    = OK-only error display
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, mode: 'error', filePath: null, message: '', backup: null });

  const removeFileFromGrid = useCallback((filePath) => {
    setSelectedFiles(prev => prev.filter(p => p !== filePath));
    setSelectedImageIds(prev => {
      const next = new Set(prev);
      next.delete(filePath);
      return next;
    });
    setImageMetadata(prev => {
      const next = { ...prev };
      delete next[filePath];
      return next;
    });
    setImageVersions(prev => {
      const next = { ...prev };
      delete next[filePath];
      return next;
    });
  }, []);

  // Put a file back after a failed delete (state captured beforehand)
  const restoreFileToGrid = useCallback((filePath, backup) => {
    setSelectedFiles(prev => prev.includes(filePath) ? prev : [...prev, filePath]);
    if (backup?.metadata) {
      setImageMetadata(prev => ({ ...prev, [filePath]: backup.metadata }));
    }
    if (backup?.version) {
      setImageVersions(prev => ({ ...prev, [filePath]: backup.version }));
    }
    if (backup?.wasSelected) {
      setSelectedImageIds(prev => new Set([...prev, filePath]));
    }
  }, []);

  // Delete flow, file-manager style: remove from the UI FIRST so the <img>
  // unmounts and Chromium releases its file handle (EBUSY otherwise), then
  // recycle. On failure the thumbnail is restored.
  const handleDeleteFile = useCallback(async (filePath) => {
    const backup = {
      metadata: imageMetadata[filePath],
      version: imageVersions[filePath],
      wasSelected: selectedImageIds.has(filePath)
    };
    removeFileFromGrid(filePath);
    try {
      const result = await window.electronAPI.trashFile(filePath);
      if (!result.success) {
        // Recycle Bin refused — offer permanent delete (file stays out of
        // the grid while the dialog is open; Cancel restores it)
        setDeleteDialog({
          isOpen: true,
          mode: 'fallback',
          filePath,
          message: 'Windows couldn\'t move this file to the Recycle Bin. Do you want to delete it permanently instead?',
          backup
        });
      }
    } catch (err) {
      console.error('Delete failed:', err);
      restoreFileToGrid(filePath, backup);
      setDeleteDialog({ isOpen: true, mode: 'error', filePath: null, message: `Could not delete the file: ${err.message}`, backup: null });
    }
  }, [imageMetadata, imageVersions, selectedImageIds, removeFileFromGrid, restoreFileToGrid]);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialog(prev => {
      // Cancelling the permanent-delete offer puts the file back
      if (prev.mode === 'fallback' && prev.filePath) {
        restoreFileToGrid(prev.filePath, prev.backup);
      }
      return { isOpen: false, mode: 'error', filePath: null, message: '', backup: null };
    });
  }, [restoreFileToGrid]);

  const handleDeletePermanent = useCallback(async () => {
    const { filePath, backup } = deleteDialog;
    setDeleteDialog({ isOpen: false, mode: 'error', filePath: null, message: '', backup: null });
    if (!filePath) return;
    try {
      const result = await window.electronAPI.deleteFilePermanent(filePath);
      if (!result.success) {
        restoreFileToGrid(filePath, backup);
        setDeleteDialog({ isOpen: true, mode: 'error', filePath: null, message: `Could not delete the file: ${result.error || 'unknown error'}`, backup: null });
      }
    } catch (err) {
      restoreFileToGrid(filePath, backup);
      setDeleteDialog({ isOpen: true, mode: 'error', filePath: null, message: `Could not delete the file: ${err.message}`, backup: null });
    }
  }, [deleteDialog, restoreFileToGrid]);

  // Prepare images data for lightbox
  const lightboxImages = sortedFiles.map(filePath => ({
    path: filePath,
    name: imageMetadata[filePath]?.fileName || filePath.split(/[/\\\\]/).pop(),
    metadata: imageMetadata[filePath],
    version: imageVersions[filePath]
  }));

  return (
    <div className="app">
      <div className="app-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>
      
      <div className="app-container">
        <Header
          onSelectFiles={selectFiles}
          onClear={clearFiles}
          disabled={isProcessing}
          mode={mode}
          licenseInfo={licenseInfo}
          onActivateLicense={() => {
            // If already has a license, try to revalidate first
            if (licenseInfo && licenseInfo.tier !== 'free') {
              revalidateCachedLicense().then(success => {
                if (!success) {
                  setShowLicenseDialog(true);
                } else {
                  alert('License validated successfully!');
                }
              });
            } else {
              setShowLicenseDialog(true);
            }
          }}
          onDeactivateLicense={handleDeactivateLicense}
        />
        
        <Controls
          settings={settings}
          onSettingsChange={setSettings}
          mode={mode}
          onModeChange={setMode}
          sortBy={sortBy}
          onSortChange={setSortBy}
          selectedCount={selectedImageIds.size}
          totalCount={selectedFiles.length}
          onSelectAll={selectAllImages}
          onProcess={requestProcess}
          onProcessAndUpload={requestProcessAndUpload}
          csvFilePath={csvFilePath}
          onOpenCSV={handleOpenCSV}
          disabled={isProcessing}
          licenseInfo={licenseInfo}
          iterations={iterations}
          setIterations={(value) => {
            setIterations(value);
            iterationsRef.current = value; // Keep ref in sync
          }}
        />

        <ImageGrid
          files={sortedFiles}
          imageMetadata={imageMetadata}
          imageVersions={imageVersions}
          selectedImageIds={selectedImageIds}
          onToggleSelection={toggleImageSelection}
          disabled={isProcessing}
          onFilesDropped={handleFilesDropped}
          onUrlsDropped={handleUrlsDropped}
          onImageClick={handleImageClick}
          onSelectFiles={selectFiles}
          onShowInFolder={handleShowInFolder}
          onDeleteFile={handleDeleteFile}
        />

        {isProcessing && (
          <ProgressBar progress={progress} message={progressMessage} />
        )}

        {importStatus && !isProcessing && (
          <ProgressBar
            title="Importing Images"
            progress={(importStatus.done / importStatus.total) * 100}
            message={
              importStatus.current
                ? `${importStatus.done} of ${importStatus.total} — reading ${importStatus.current}`
                : `${importStatus.done} of ${importStatus.total}`
            }
          />
        )}

        <ConfirmationDialog
          isOpen={deleteDialog.isOpen}
          onConfirm={deleteDialog.mode === 'fallback' ? handleDeletePermanent : closeDeleteDialog}
          onCancel={closeDeleteDialog}
          title={deleteDialog.mode === 'fallback' ? 'Delete Permanently?' : 'Delete Failed'}
          message={deleteDialog.message}
          warning={deleteDialog.mode === 'fallback'
            ? 'Permanent deletion cannot be undone.'
            : 'The file was not changed.'}
          confirmLabel={deleteDialog.mode === 'fallback' ? 'Delete Permanently' : 'OK'}
          showCancel={deleteDialog.mode === 'fallback'}
        />

        <CustomPromptModal
          isOpen={fillPromptModal}
          onClose={() => setFillPromptModal(false)}
          onSave={handleFillPromptSave}
          initialPrompt={settings.useSystemPrompt ? '' : settings.customPrompt}
        />

        <ConfirmationDialog
          isOpen={overwriteConfirm.isOpen}
          onConfirm={handleOverwriteConfirm}
          onCancel={() => setOverwriteConfirm({ isOpen: false, action: null })}
          title="Overwrite Original Files"
          message={`Overwrite Originals is ON. The ${selectedImageIds.size} selected image${selectedImageIds.size === 1 ? '' : 's'} will be exported back into their original folders under their original names — permanently replacing the source files where the format matches.`}
          warning="Replaced files cannot be recovered."
          confirmLabel="Overwrite and Export"
        />

        <ConfirmationDialog
          isOpen={showResetDialog}
          onConfirm={handleResetConfirm}
          onCancel={handleResetCancel}
        />

        <LicenseDialog
          isOpen={showLicenseDialog}
          onClose={handleLicenseDialogClose}
          onLicenseValidated={handleLicenseValidated}
        />

        <SuccessDialog
          isOpen={successDialog.isOpen}
          onClose={handleSuccessClose}
          onViewInFolder={handleViewInFolder}
          message={successDialog.message}
          showViewInFolder={!!successDialog.outputPath}
        />

        <ValidationDialog
          isOpen={validationDialog.isOpen}
          message={validationDialog.message}
          onClose={() => setValidationDialog({ isOpen: false, message: '' })}
        />

        <AILimitDialog
          isOpen={aiLimitDialog.isOpen}
          limitInfo={aiLimitDialog.limitInfo}
          onClose={() => setAiLimitDialog({ isOpen: false, limitInfo: null })}
        />
      </div>

      <Lightbox
        isOpen={lightboxOpen}
        currentIndex={lightboxIndex}
        images={lightboxImages}
        onClose={handleLightboxClose}
        onNavigate={handleLightboxNavigate}
        onRetouched={handleRetouched}
      />
    </div>
  );
}

export default App;